import { useRouter } from 'next/router';

import { Anchor, Box, Divider, Heading, List } from '@zillow/constellation-10';

import { LoadingParagraph, LoadingText, shimmerStyles } from '@zg-rentals/loading-components';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import accessUtils from '../../../../utils/accessUtils';

import { TRIGGER_LOCATIONS } from '../../../../constants/ClickstreamConstants';

import SFCostAndFeesTotalMonthlyPriceWithFeeAlert from '../../../../components/Alerts/SFCostAndFeesTotalMonthlyPriceWithFeeAlert';

import {
  wizardFlexContainerStyles,
  wizardFormSectionContainerStyles,
  wizardHeadingStyles,
  wizardParagraphStyles,
} from '../../../../styles/wizard';

import { emitLearnMoreEvent } from '../../../../analytics/events/singleFamilyTotalMonthlyPrice';
import { useAuth } from '../../../AuthProvider';
import CostsAndFeesFormField from '../../../PostingPath/CostsAndFeesFormField';
import SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert from '../../../SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert';
import WizardTipCardContainer from '../../../WizardTipCardContainer';

import type { ShowDesktopViewBreakpoint } from '../../../WizardStepController';

const FeeCategoryLoadingSkeleton = () => {
  return (
    <>
      <Box css={{ paddingBlock: '400', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
          <Box data-loading css={{ width: 'icon.md', height: 'icon.md', ...shimmerStyles }} />
          <LoadingText textStyle="body-bold" isLoading loadingWidth="200px">
            Category name
          </LoadingText>
        </Box>
        <LoadingText isLoading loadingWidth="50px">
          Add button
        </LoadingText>
      </Box>
      <Divider />
    </>
  );
};

export const CostsAndFeesStepContent = ({
  showDesktopViewBreakpoint = 'xl',
  triggerLocation,
}: {
  triggerLocation: 'posting_path' | 'zrm_app_webview';
  showDesktopViewBreakpoint?: ShowDesktopViewBreakpoint;
}) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { user } = useAuth();
  const { data: listingDetails, isLoading } = useGetListingDetails(listingAlias);

  const isTotalMonthlyPriceWithFeeEligible = listingDetails?.totalMonthlyPriceWithFeeNotice?.isEligible ?? false;
  const isOptInTotalMonthlyPriceWithFeeEligible = listingDetails?.totalMonthlyPriceWithFee?.isOptInEligible ?? false;

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  return (
    <div data-testid="CostsAndFees">
      <Box css={wizardFlexContainerStyles}>
        <Box
          data-testid="FormSectionContainer"
          css={wizardFormSectionContainerStyles.raw({ isInMultiplexListingFlow: false })}
        >
          <Heading
            textStyle="heading-sm"
            level={triggerLocation === TRIGGER_LOCATIONS.ZRM_APP_WEBVIEW ? 1 : 2}
            css={wizardHeadingStyles}
          >
            What additional fees do you charge?
          </Heading>

          <LoadingParagraph isLoading={isLoading} css={wizardParagraphStyles}>
            {isOptInTotalMonthlyPriceWithFeeEligible ? (
              <>
                These details help renters know the real cost of renting your property, and helps you meet relevant fee
                transparency laws.{' '}
              </>
            ) : (
              <>
                We&apos;ve highlighted some of the most common fee{' '}
                {canAccessTotalMonthlyPriceWithFee ? 'types' : 'categories'}. These details help renters know the real
                cost of renting your property before they apply.
              </>
            )}
          </LoadingParagraph>

          {canAccessTotalMonthlyPriceWithFee && <SFCostAndFeesTotalMonthlyPriceWithFeeAlert />}
          {isOptInTotalMonthlyPriceWithFeeEligible && (
            <SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert triggerLocation={triggerLocation} />
          )}

          {isLoading ? (
            Array.from({ length: 4 }).map((_, index) => <FeeCategoryLoadingSkeleton key={index} />)
          ) : (
            <CostsAndFeesFormField displayedLocation={triggerLocation} />
          )}
        </Box>

        <WizardTipCardContainer
          header="Tips for adding costs and fees"
          showDesktopViewBreakpoint={showDesktopViewBreakpoint}
        >
          <div data-testid="WizardTipContent">
            <List.Root appearance="bullet">
              {canAccessTotalMonthlyPriceWithFee || isOptInTotalMonthlyPriceWithFeeEligible ? (
                <>
                  {canAccessTotalMonthlyPriceWithFee && (
                    <>
                      <List.Item>
                        Make sure to add any required monthly fees, and note if any are already included in the base
                        rent.
                      </List.Item>

                      <List.Item>
                        Fee transparency helps renters make informed decisions and improves efficiency for you when
                        placing a renter.{' '}
                        <Anchor
                          target="_blank"
                          href="https://www.zillow.com/rentals-network/pricing-transparency/"
                          rel="noopener noreferrer"
                          onClick={() => {
                            if (listingDetails && user) {
                              emitLearnMoreEvent({
                                currentProperty: listingDetails,
                                user,
                                triggerObject: 'costs_and_fees',
                                triggerLocation,
                              });
                            }
                          }}
                        >
                          Learn more
                        </Anchor>{' '}
                        about regulations in your area.
                      </List.Item>
                    </>
                  )}

                  {isOptInTotalMonthlyPriceWithFeeEligible && (
                    <>
                      <List.Item>
                        Required monthly fees can be included in your base rent or added separately here.
                      </List.Item>

                      <List.Item>
                        Once your required monthly fees are accounted for, we suggest opting to show the total monthly
                        price on your listing.
                      </List.Item>
                    </>
                  )}

                  <List.Item>Wait to add application fees — we&apos;ll ask about those last.</List.Item>
                </>
              ) : (
                <>
                  <List.Item>Wait to add application fees — we&apos;ll ask about those last.</List.Item>

                  <List.Item>Only add the fees you need, and note which are included in the base rent.</List.Item>

                  <List.Item>
                    Some states require disclosing certain fees — be sure to check what&apos;s required for your area.
                  </List.Item>
                </>
              )}
            </List.Root>
          </div>
        </WizardTipCardContainer>
      </Box>
    </div>
  );
};
